/**
 * E2E Interactions Page
 * Issue #331: Dedicated page for viewing and managing E2E User Interactions
 * Issue #351: Updated terminology - "interactions" for user actions, "E2E flows" for the journey
 * Issue #355: Open canvas directly from Interactions page, remove sidebar
 * Issue #356: Add back navigation from canvas to Interactions page
 *
 * Features:
 * - InteractionCatalog for browsing all interactions (catalog view)
 * - E2EFlowRenderer full-width canvas for viewing selected interaction's E2E flow (diagram view)
 * - Clean back navigation from diagram to catalog
 */

import { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useParams, Link, useSearchParams, useLocation } from 'react-router-dom';
import type { Node, Edge } from '@xyflow/react';
import type { E2EFlowPath, CodeEvidence, EdgeInfo } from '@veodiagram/shared';
import { api, apiFetch } from '../services/api';
import { E2EFlowRenderer, type E2EFlowRendererHandle, type NodePositions, type LayoutMode, calculateFlowStats } from '../components/diagram/renderers/e2eFlow';
import { InteractionCatalog } from '../components/diagram/renderers/e2eFlow/InteractionCatalog';
import {
  CallersEndpointsPanel,
  CallersEndpointsCollapsedStrip,
} from '../components/diagram/renderers/e2eFlow/CallersEndpointsPanel';
import { BulkAINamingModal, type BulkNamingResult } from '../components/interactions/BulkAINamingModal';
import { AINamingSettings } from '../components/interactions/AINamingSettings';
import { FeedbackHistoryPanel } from '../components/interactions/FeedbackHistoryPanel';
import { AIFlowEnhancementModal, type EnhancementCompletionInfo } from '../components/interactions/AIFlowEnhancementModal';
import { EvidencePanel } from '../components/diagram/EvidencePanel';
import { EdgeDetailPanel } from '../components/diagram/EdgeDetailPanel';
import { DiagramInfoPanel } from '../components/diagram/DiagramInfoPanel';
import { useResolvedNames } from '../hooks/useInteractionNaming';
import { E2EFlowVersionHistory, E2EFlowExportMenu } from '../components/e2eFlow';
import { StitchReviewBadge } from '../components/stitching/StitchReviewEntry';
import { ClickStitchReviewBadge } from '../components/stitching/ClickStitchReviewEntry';
import { useCatalog } from '../hooks/useCatalog';

// Issue #404: Type for node with evidence data (matching EvidencePanel interface)
interface NodeWithEvidence {
  id: string;
  label: string;
  type?: string;
  description?: string;
  evidence?: CodeEvidence[];
  e2eStepData?: {
    stepType: string;
    method?: string;
    endpoint?: string;
    operation?: string;
    table?: string;
    model?: string;
    databaseStepData?: Record<string, unknown>;
    apiCallStepData?: Record<string, unknown>;
    apiRouteStepData?: Record<string, unknown>;
    backendLogicStepData?: Record<string, unknown>;
    sourceFile?: string;
    sourceLine?: number;
  };
}

// Issue #355: Simplified view modes - catalog or diagram only
type ViewMode = 'catalog' | 'diagram';

/** Issue #784: Route that exists but isn't linked to any user interaction flow */
interface SystemRoute {
  path: string;
  methods: string[];
  handlerFunction: string;
  sourceFile: string;
  sourceLine: number;
  framework: string;
  invokedFromCode: boolean;
  purpose?: string;
  // veodiagram-issues#63 — set when the route's handler calls
  // `response.render('X.njk', ...)`. Surfaces in the SystemRoutes
  // table as a "→ <screenName>" annotation.
  screenName?: string;
  templatePath?: string;
}

/**
 * veodiagram-issues#63 — server-rendered screen detected via
 * `response.render('X.njk', vars)`. Distinct from `SystemRoute`
 * because a single route can multiplex multiple templates (query-
 * mode multiplexers like `auth/actions` switching between
 * `new-password.njk` / `verify-email.njk` / etc. by `request.query.mode`).
 */
interface ScreenDetected {
  routePath: string;
  templatePath: string;
  screenName: string;
  handlerFile: string;
  renderLine: number;
}

/**
 * veodiagram#1060 RFC Phase 3 — new-pipeline flow shape.
 *
 * The new 5-stage flow subsystem emits a tighter `Flow` record than the
 * legacy `E2EFlowPath`. The fields below are the subset needed for the
 * UI flip's catalog view; the full pipeline shape lives in
 * `apps/api/src/services/analysis/flowSubsystem/types.ts`.
 *
 * Surfaced via `GET /projects/:id/e2e-flows` as `newPipelineFlows` and
 * `newPipelineStats` whenever the project was last regenerated with
 * `?flowSubsystem=new` (dual-emit). `null` when dual-emit was off.
 */
interface NewPipelineFlow {
  id: string;
  trigger: {
    file: string;
    line: number;
    attributeName: string;
    elementName: string;
    elementKind: 'host' | 'component';
  };
  triggerType: string;
  resolvedHandler: { kind: string };
  steps: Array<{ type: string; line: number; [key: string]: unknown }>;
}

interface NewPipelineStats {
  triggersDiscovered: number;
  handlersResolved: number;
  handlersUnresolved: number;
  stepsEmitted: number;
  durationMs: number;
  parsedFileCount: number;
  parseFailures: number;
}

interface E2EFlowsData {
  diagramId: string | null;
  diagramName: string;
  flows: E2EFlowPath[];
  customNodePositions: NodePositions | null;  // Issue #389: Saved layout positions
  systemRoutes?: SystemRoute[];  // Issue #784: System-triggered routes
  // veodiagram-issues#63 — server-rendered pages detected by
  // `templateRenderDetector`. Empty array means "detector ran with
  // 0 hits" (e.g. SPA project); undefined means "detector never ran"
  // (no localPath available).
  screensDetected?: ScreenDetected[];
  // veodiagram#1060 RFC Phase 3 — dual-emit output from the new flow
  // subsystem. `null` when dual-emit was off at the last regeneration.
  newPipelineFlows?: NewPipelineFlow[] | null;
  newPipelineStats?: NewPipelineStats | null;
  updatedAt: string;
  // Issue #982: non-null when the user mutated click stitches after
  // the last flow regeneration. The banner reads this.
  e2eFlowsStaleSince?: string | null;
}

interface Project {
  id: string;
  name: string;
}

/**
 * Issue #784: Collapsible section showing routes that exist but aren't
 * triggered by any user interaction (e.g., auth hooks, background jobs,
 * webhook handlers, cron-triggered edge functions).
 */
/**
 * #1074 G1 (#648): format an E2E flow as Markdown for the "Copy as LLM
 * Context" button. The output is intentionally pastable directly into an
 * LLM prompt — flow name, trigger, ordered steps with type + label +
 * file:line evidence. No JSON, no internal IDs, no implementation noise.
 */
function formatFlowAsMarkdown(flow: E2EFlowPath): string {
  const lines: string[] = [];
  lines.push(`# ${flow.name || 'Unnamed flow'}`);
  if (flow.triggerType || flow.triggerName) {
    lines.push('');
    lines.push(`**Trigger:** ${flow.triggerType ?? '?'} → ${flow.triggerName ?? '?'}`);
  }
  if (flow.handlerName) {
    lines.push(`**Handler:** ${flow.handlerName}`);
  }
  lines.push('');
  lines.push('## Steps');
  lines.push('');
  flow.steps?.forEach((step, idx) => {
    const stepData = step as unknown as { type?: string; label?: string; name?: string; sourceFile?: string; sourceLine?: number };
    const label = stepData.label || stepData.name || stepData.type || 'step';
    const loc = stepData.sourceFile
      ? ` _(${stepData.sourceFile}${stepData.sourceLine ? `:${stepData.sourceLine}` : ''})_`
      : '';
    lines.push(`${idx + 1}. **${stepData.type ?? '?'}** — ${label}${loc}`);
  });
  return lines.join('\n');
}

/**
 * veodiagram-issues#63 — list of server-rendered pages with their
 * template paths and render-line links back into the source. Mirrors
 * the SystemRoutesSection shape so the two sit side-by-side.
 *
 * Empty array (detector ran with no hits) renders nothing — same
 * behavior as SystemRoutesSection when `routes` is empty. The
 * presence/absence of the field on the API response is a meaningful
 * signal but only at the data layer; users on SPA projects don't
 * need to see "no screens" copy.
 */
function ScreensSection({ screens }: { screens: ScreenDetected[] }) {
  const [expanded, setExpanded] = useState(false);
  if (screens.length === 0) return null;
  // Distinct screens (by templatePath) — query-mode multiplexers
  // emit one entry per render call, so the same template can appear
  // multiple times.
  const uniqueTemplates = new Set(screens.map(s => s.templatePath)).size;

  return (
    <div className="mx-4 mb-4 border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 dark:bg-slate-800/50 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors text-left"
      >
        <div className="flex items-center gap-2">
          <svg className={`w-4 h-4 transition-transform ${expanded ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
          <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
            Server-Rendered Screens
          </span>
          <span className="text-xs px-2 py-0.5 rounded-full bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-400">
            {screens.length}
          </span>
        </div>
        <span className="text-xs text-slate-500 dark:text-slate-400">
          {uniqueTemplates} template{uniqueTemplates !== 1 ? 's' : ''}
        </span>
      </button>
      {expanded && (
        <div className="divide-y divide-slate-100 dark:divide-slate-700/50">
          {screens.map((screen, i) => (
            <div key={`${screen.handlerFile}-${screen.renderLine}-${i}`} className="px-4 py-2.5 flex items-start gap-3 text-sm">
              <span className="mt-0.5 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-mono bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400">
                page
              </span>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-slate-800 dark:text-slate-200 truncate" title={screen.screenName}>
                  {screen.screenName}
                </div>
                <div className="flex items-center gap-2 mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                  <span className="font-mono truncate" title={screen.routePath}>
                    {screen.routePath || '(no route)'}
                  </span>
                  <span>·</span>
                  <span className="font-mono truncate" title={screen.templatePath}>
                    {screen.templatePath.split('/').pop()}
                  </span>
                  <span>·</span>
                  <span className="font-mono truncate" title={`${screen.handlerFile}:${screen.renderLine}`}>
                    {screen.handlerFile.split('/').slice(-2).join('/')}:{screen.renderLine}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SystemRoutesSection({
  routes,
  screens = [],
}: {
  routes: SystemRoute[];
  // veodiagram-issues#64 R1#3 — pass screensDetected through so we
  // can count multiplexed renders per route (auth/actions renders
  // 5 templates by switch-on-query-mode; the badge should say
  // "+4 more" rather than misleadingly showing only the latest one).
  screens?: ScreenDetected[];
}) {
  const [expanded, setExpanded] = useState(false);
  const invokedCount = routes.filter(r => r.invokedFromCode).length;
  const uninvokedCount = routes.length - invokedCount;

  // Map route.path → set of distinct screen names rendered by handlers
  // at that path. Multi-template routes get a "+N more" suffix on
  // the inline badge.
  //
  // R1#8 fix — screen entries carry a routePath with a query string
  // suffix when the analyzer detected query-mode multiplexing (e.g.
  // `/views/auth/actions?mode=resetPassword`), but SystemRoute.path
  // is the bare path. Normalize to the path-only form so they join.
  const screensByRoute = new Map<string, Set<string>>();
  for (const s of screens) {
    if (!s.routePath) continue;
    // R2#F2 — also strip `#` so hash-fragmented route paths
    // (rare but technically valid) join correctly. Matches the
    // same defensive policy as the `?` strip above.
    const basePath = s.routePath.split(/[?#]/)[0];
    const set = screensByRoute.get(basePath) ?? new Set<string>();
    set.add(s.screenName);
    screensByRoute.set(basePath, set);
  }

  return (
    <div className="mx-4 mb-4 border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 dark:bg-slate-800/50 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors text-left"
      >
        <div className="flex items-center gap-2">
          <svg className={`w-4 h-4 transition-transform ${expanded ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
          <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
            System-Triggered Functions
          </span>
          <span className="text-xs px-2 py-0.5 rounded-full bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-400">
            {routes.length}
          </span>
        </div>
        <span className="text-xs text-slate-500 dark:text-slate-400">
          {uninvokedCount > 0 && `${uninvokedCount} uninvoked`}
          {uninvokedCount > 0 && invokedCount > 0 && ', '}
          {invokedCount > 0 && `${invokedCount} code-invoked`}
        </span>
      </button>
      {expanded && (
        <div className="divide-y divide-slate-100 dark:divide-slate-700/50">
          {routes.map((route, i) => (
            <div key={i} className="px-4 py-2.5 flex items-start gap-3 text-sm">
              <span className={`mt-0.5 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-mono ${
                route.invokedFromCode
                  ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400'
                  : 'bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400'
              }`}>
                {route.methods.join('/')}
              </span>
              <div className="flex-1 min-w-0">
                <div className="font-mono text-slate-800 dark:text-slate-200 truncate flex items-center gap-2">
                  <span className="truncate">{route.path}</span>
                  {/* veodiagram-issues#63 — surface the rendered page
                      name inline so the user sees route→screen pairing
                      at a glance.
                      R1#3 — when the route multiplexes (e.g.
                      auth/actions rendering new-password.njk /
                      verify-email.njk / etc. via switch on query.mode),
                      the badge would otherwise show ONE name and hide
                      the rest. Show "+N more" so the user knows there
                      are additional templates listed below in
                      ScreensSection. */}
                  {route.screenName && (() => {
                    const screenSet = screensByRoute.get(route.path);
                    const extra = screenSet ? Math.max(0, screenSet.size - 1) : 0;
                    return (
                      <span
                        className="text-xs px-1.5 py-0.5 rounded bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 font-sans font-medium flex-shrink-0"
                        title={
                          extra > 0
                            ? `Renders ${(screenSet?.size ?? 1)} templates — see Screens section`
                            : route.templatePath || route.screenName
                        }
                      >
                        → {route.screenName}{extra > 0 ? ` +${extra} more` : ''}
                      </span>
                    );
                  })()}
                </div>
                <div className="flex items-center gap-2 mt-0.5">
                  {route.purpose && (
                    <span className="text-xs text-slate-500 dark:text-slate-400">{route.purpose}</span>
                  )}
                  {!route.purpose && (
                    <span className="text-xs text-slate-400 dark:text-slate-500 truncate">
                      {route.sourceFile.split('/').slice(-2).join('/')}:{route.sourceLine}
                    </span>
                  )}
                  {route.invokedFromCode ? (
                    <span className="text-xs text-amber-600 dark:text-amber-400">invoked from code, not interaction-linked</span>
                  ) : (
                    <span className="text-xs text-slate-400 dark:text-slate-500">not invoked from application code</span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function E2EFlowsPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  // Issue #647: Deep-link to a specific flow via ?flowId param
  const deepLinkedFlowId = searchParams.get('flowId');
  // Issue #859: ?filter=clean shows only flows where all stitches are confident
  const filterParam = searchParams.get('filter');
  // Legacy `?flowSubsystem` URL flag is gone — the legacy generator
  // was deleted in #1060-9B and the toggle was vestigial. The new
  // subsystem is the only producer; its output flows through
  // `flowRenderAdapter` into `flowsData.flows` regardless.

  // #1310 PR-J — fetch the graph-backed catalog endpoint when
  // ?catalog=1 is set on the URL. Used today only for the
  // classification badge counts (read-only foundation); the full
  // InteractionCatalog prop migration lands in PR-J2.
  const useCatalogEndpoint = searchParams.get('catalog') === '1';
  const { catalog: catalogData } = useCatalog(useCatalogEndpoint ? projectId : undefined);
  const breadcrumb = (location.state as { breadcrumb?: Array<{label: string; url: string}> } | null)?.breadcrumb;
  const [project, setProject] = useState<Project | null>(null);
  const [flowsData, setFlowsData] = useState<E2EFlowsData | null>(null);
  const [selectedFlowIndex, setSelectedFlowIndex] = useState<number | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('catalog'); // Issue #355: Start with catalog only
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const rendererRef = useRef<E2EFlowRendererHandle>(null);  // Issue #354: Ref for view finder
  const [showBulkNaming, setShowBulkNaming] = useState(false);  // Issue #363: Bulk AI naming modal
  const [showAISettings, setShowAISettings] = useState(false);  // Issue #364: AI naming settings
  const [showFeedbackHistory, setShowFeedbackHistory] = useState(false);  // Issue #365: Feedback history
  const [namesRefreshTrigger, setNamesRefreshTrigger] = useState(0);  // Trigger to refresh names in InteractionCatalog
  const [showEnhancement, setShowEnhancement] = useState(false);  // Issue #392: AI flow enhancement modal
  // #1074 G1 (#648): tiny in-button "Copied!" feedback for the LLM-context copy.
  const [copyFeedback, setCopyFeedback] = useState<string | null>(null);
  const [showVersionHistory, setShowVersionHistory] = useState(false);  // Issue #415: Version history modal
  const [isRegenerating, setIsRegenerating] = useState(false);  // Issue #415: Regeneration in progress
  const [isRegeneratingAll, setIsRegeneratingAll] = useState(false);  // Issue #982: full-project regen from stale banner
  const [viewingHistoricalVersion, setViewingHistoricalVersion] = useState<{
    versionId: string;
    version: number;
    flowData: E2EFlowPath;
    createdAt: string;
  } | null>(null);  // Issue #416: Viewing historical version

  // Issue #404: Evidence panel state for showing full node details
  const [selectedNode, setSelectedNode] = useState<NodeWithEvidence | null>(null);
  const [isEvidencePanelOpen, setIsEvidencePanelOpen] = useState(false);

  // Issue #434: Edge detail panel state for showing HTTP request/response details
  const [selectedEdge, setSelectedEdge] = useState<EdgeInfo | null>(null);
  const [isEdgePanelOpen, setIsEdgePanelOpen] = useState(false);

  // Issue #404: Info panel, direction control, legend, and node finder state
  const [isInfoPanelOpen, setIsInfoPanelOpen] = useState(false);
  const [showLegend, setShowLegend] = useState(true);
  const [showNodeFinder, setShowNodeFinder] = useState(false);
  const [hasLayoutChanges, setHasLayoutChanges] = useState(false);
  // Issue #427: Swimlane layout mode state
  const [layoutMode, setLayoutMode] = useState<LayoutMode>('swimlane');

  // Issue #389: Track custom node positions for layout persistence
  // Separate per-mode positions so toggling modes doesn't lose saved layouts
  const [customPositions, setCustomPositions] = useState<NodePositions | undefined>(undefined);
  const sequentialPositionsRef = useRef<NodePositions | undefined>(undefined);
  const swimlanePositionsRef = useRef<NodePositions | undefined>(undefined);

  // Issue #411: Track AI enhancement info per flow for display in info panel
  const [flowEnhancementInfo, setFlowEnhancementInfo] = useState<Record<string, EnhancementCompletionInfo>>({});

  // Issue #556 + #996: Callers & Endpoints panel state. Defaults to
  // open per #996; the explicit user collapse preference persists
  // per-project via localStorage so we don't fight the user across
  // reloads.
  const [showCallersEndpoints, setShowCallersEndpoints] = useState<boolean>(() => {
    if (!projectId) return true;
    const stored = localStorage.getItem(`e2e-flows-show-callers-endpoints-${projectId}`);
    return stored === null ? true : stored === '1';
  });
  useEffect(() => {
    if (!projectId) return;
    localStorage.setItem(
      `e2e-flows-show-callers-endpoints-${projectId}`,
      showCallersEndpoints ? '1' : '0',
    );
  }, [showCallersEndpoints, projectId]);
  const [activeCaller, setActiveCaller] = useState<string | null>(null);
  const [activeEndpoint, setActiveEndpoint] = useState<string | null>(null);

  // Issue #377: Use resolved names hook to get updated interaction names
  const { names: resolvedNames, fetchAllNames } = useResolvedNames(projectId || '');

  // Issue #377: Fetch resolved names when component mounts and when namesRefreshTrigger changes
  useEffect(() => {
    if (projectId) {
      fetchAllNames();
    }
  }, [projectId, namesRefreshTrigger, fetchAllNames]);

  // Issue #377: Apply resolved names to flows for display in diagram view
  const flowsWithResolvedNames = useMemo(() => {
    if (!flowsData?.flows) return flowsData;

    return {
      ...flowsData,
      flows: flowsData.flows.map(flow => {
        const resolved = resolvedNames[flow.id];
        if (resolved?.name) {
          return { ...flow, name: resolved.name };
        }
        return flow;
      })
    };
  }, [flowsData, resolvedNames]);

  // Issue #542: Internal interaction visibility toggle (defaults to hidden)
  const [showInternalInteractions, setShowInternalInteractions] = useState(false);

  // Issue #859 / #878 + #1339 item 5 — flow filter, four buckets now:
  // clean / needs-review / no-api / dangling. `?filter=dirty` is kept
  // as an alias for `needs-review` so existing deep links still work.
  // `dangling` flows have api-calls with no matching backend route —
  // the actionable signal is "stitcher couldn't resolve" vs.
  // needs-review's "stitch matched but heuristic".
  type FlowFilter = 'all' | 'clean' | 'needs-review' | 'no-api' | 'dangling';
  const [flowFilter, setFlowFilter] = useState<FlowFilter>(() => {
    if (filterParam === 'clean') return 'clean';
    if (filterParam === 'dirty' || filterParam === 'needs-review') return 'needs-review';
    if (filterParam === 'no-api') return 'no-api';
    if (filterParam === 'dangling') return 'dangling';
    return 'all';
  });

  // Issue #878 + #1339 item 5: read the four-state `classification`
  // field, falling back to the legacy `isClean` for older flows.
  const getClassification = (flow: any): 'clean' | 'needs-review' | 'no-api' | 'dangling' | undefined => {
    if (
      flow.classification === 'clean' ||
      flow.classification === 'needs-review' ||
      flow.classification === 'no-api' ||
      flow.classification === 'dangling'
    ) {
      return flow.classification;
    }
    if (typeof flow.isClean === 'boolean') {
      return flow.isClean ? 'clean' : 'needs-review';
    }
    return undefined;
  };

  // Issue #542: Filter internal interactions unless the user has opted in
  const visibleFlowsData = useMemo(() => {
    if (!flowsWithResolvedNames?.flows) return flowsWithResolvedNames;
    if (showInternalInteractions) return flowsWithResolvedNames;
    return {
      ...flowsWithResolvedNames,
      flows: flowsWithResolvedNames.flows.filter(f => !f.isInternal),
    };
  }, [flowsWithResolvedNames, showInternalInteractions]);

  const internalInteractionCount = useMemo(
    () => flowsWithResolvedNames?.flows.filter(f => f.isInternal).length ?? 0,
    [flowsWithResolvedNames]
  );

  // Issue #556: Filter flows for catalog based on active caller/endpoint selection
  // Issue #859: also apply clean/dirty filter.
  const filteredCatalogFlows = useMemo(() => {
    if (!visibleFlowsData?.flows) return [];
    let flows = visibleFlowsData.flows;
    // Issue #878: three-way filter
    if (flowFilter === 'clean') {
      flows = flows.filter(f => getClassification(f) === 'clean');
    } else if (flowFilter === 'needs-review') {
      flows = flows.filter(f => getClassification(f) === 'needs-review');
    } else if (flowFilter === 'no-api') {
      flows = flows.filter(f => getClassification(f) === 'no-api');
    } else if (flowFilter === 'dangling') {
      flows = flows.filter(f => getClassification(f) === 'dangling');
    }
    if (activeCaller) {
      flows = flows.filter(f =>
        f.apiCalls?.some(c => `${c.sourceFile}|${c.sourceContext ?? ''}` === activeCaller)
      );
    }
    if (activeEndpoint) {
      flows = flows.filter(f =>
        f.apiCalls?.some(c => `${c.method} ${c.endpoint}` === activeEndpoint)
      );
    }
    return flows;
  }, [visibleFlowsData?.flows, activeCaller, activeEndpoint, flowFilter]);

  // Fetch project and flows data
  useEffect(() => {
    if (!projectId) return;

    const fetchData = async () => {
      setIsLoading(true);
      setError(null);

      try {
        // Fetch project details
        const projectData = await api.getProject(projectId);
        setProject(projectData);

        // Fetch E2E flows
        const response = await apiFetch(`/projects/${projectId}/e2e-flows`, {
          credentials: 'include',
        });

        if (!response.ok) {
          throw new Error('Failed to fetch E2E flows');
        }

        const data = await response.json();
        setFlowsData(data);
        // Issue #389: Initialize custom positions from saved layout
        if (data.customNodePositions) {
          setCustomPositions(data.customNodePositions);
          // Seed both mode refs — each converter ignores irrelevant keys
          sequentialPositionsRef.current = data.customNodePositions;
          swimlanePositionsRef.current = data.customNodePositions;
        }
        // Don't auto-select - wait for user to click a catalog item
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load flows');
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [projectId]);

  // Issue #647: Auto-select and show flow when navigated with ?flowId deep link
  // The flowId may be either a flow.id or a UserInteraction.id (sourceInteractionId)
  useEffect(() => {
    if (!deepLinkedFlowId || !flowsData) return;
    const idx = flowsData.flows.findIndex(
      f => f.id === deepLinkedFlowId || f.sourceInteractionId === deepLinkedFlowId
    );
    if (idx !== -1) {
      setSelectedFlowIndex(idx);
      setViewMode('diagram');
    }
  }, [deepLinkedFlowId, flowsData]);

  // Issue #355: Handle flow selection - switch to full-width diagram view
  const handleFlowSelect = useCallback((flow: E2EFlowPath) => {
    if (!flowsData) return;
    const index = flowsData.flows.findIndex(f => f.id === flow.id);
    if (index !== -1) {
      setSelectedFlowIndex(index);
      // Switch to full-width diagram view (no sidebar)
      setViewMode('diagram');
    }
  }, [flowsData]);

  // Issue #356: Handle back navigation from diagram to catalog
  const handleBackToCatalog = useCallback(() => {
    setViewMode('catalog');
    // Keep selection so user can see what was selected when returning
  }, []);

  // Issue #363: Handle bulk naming results
  const handleBulkNamingComplete = useCallback((results: BulkNamingResult[]) => {
    // Update local flow names with accepted results
    if (flowsData) {
      const updatedFlows = flowsData.flows.map(flow => {
        const result = results.find(r => r.interactionId === flow.id && r.userAction === 'accepted');
        if (result) {
          return { ...flow, name: result.finalName };
        }
        return flow;
      });
      setFlowsData({ ...flowsData, flows: updatedFlows });
    }
    setShowBulkNaming(false);
    // Trigger InteractionCatalog to refetch names from backend
    setNamesRefreshTrigger(prev => prev + 1);
  }, [flowsData]);

  // Issue #362: Handle individual name updates from InteractionCatalog
  const handleNameUpdated = useCallback((flowId: string, newName: string) => {
    if (flowsData) {
      const updatedFlows = flowsData.flows.map(flow =>
        flow.id === flowId ? { ...flow, name: newName } : flow
      );
      setFlowsData({ ...flowsData, flows: updatedFlows });
    }
  }, [flowsData]);

  // Issue #389: Handle layout changes from diagram renderer
  // Only update refs (not state) to avoid re-rendering the renderer via prop changes,
  // which interferes with React Flow's internal drag tracking after the first drag.
  const handleLayoutChange = useCallback((positions: NodePositions) => {
    setHasLayoutChanges(true);
    if (layoutMode === 'sequential') {
      sequentialPositionsRef.current = positions;
    } else {
      swimlanePositionsRef.current = positions;
    }
  }, [layoutMode]);

  // Issue #389: Save layout to backend
  // Get positions from the renderer ref (source of truth during editing)
  const handleSaveLayout = useCallback(async () => {
    if (!projectId || !flowsData?.diagramId) return;

    const positions = rendererRef.current?.getNodePositions();
    if (!positions || Object.keys(positions).length === 0) return;

    try {
      await api.updateE2EFlowLayout(projectId, flowsData.diagramId, positions);
      // Update flowsData to reflect saved positions (so they persist on reload)
      setFlowsData(prev => prev ? { ...prev, customNodePositions: positions } : prev);
      // Sync state and refs so mode toggling has the latest saved positions
      setCustomPositions(positions);
      if (layoutMode === 'sequential') {
        sequentialPositionsRef.current = positions;
      } else {
        swimlanePositionsRef.current = positions;
      }
      // Clear the "Layout modified" indicator in the renderer and page
      rendererRef.current?.clearLayoutChanges();
      setHasLayoutChanges(false);
    } catch (err) {
      console.error('Failed to save layout:', err);
      setError('Failed to save layout');
    }
  }, [projectId, flowsData?.diagramId, layoutMode]);

  // Issue #404: Reset layout to auto-calculated positions
  const handleResetLayout = useCallback(() => {
    rendererRef.current?.resetToAutoLayout();
    setHasLayoutChanges(false);
  }, []);

  // Issue #404: Handle node click to show full details in EvidencePanel
  const handleNodeClick = useCallback((node: Node) => {
    // Skip evidence panel for swimlane layout nodes
    if (node.type === 'swimlaneHeader') {
      return;
    }

    const nodeData = node.data as Record<string, unknown>;
    const stepType = nodeData.type as string | undefined;

    // Build e2eStepData if we have a step type
    let e2eStepData: NodeWithEvidence['e2eStepData'] = undefined;
    if (stepType && typeof stepType === 'string') {
      e2eStepData = {
        stepType,
        method: nodeData.method as string | undefined,
        endpoint: nodeData.endpoint as string | undefined,
        operation: nodeData.operation as string | undefined,
        table: nodeData.table as string | undefined,
        model: nodeData.model as string | undefined,
        databaseStepData: nodeData.databaseStepData as Record<string, unknown> | undefined,
        apiCallStepData: nodeData.apiCallStepData as Record<string, unknown> | undefined,
        apiRouteStepData: nodeData.apiRouteStepData as Record<string, unknown> | undefined,
        backendLogicStepData: nodeData.backendLogicStepData as Record<string, unknown> | undefined,
        sourceFile: nodeData.sourceFile as string | undefined,
        sourceLine: nodeData.sourceLine as number | undefined,
      };
    }

    const nodeWithEvidence: NodeWithEvidence = {
      id: node.id,
      label: (nodeData.label as string) || node.id,
      type: node.type,
      description: nodeData.description as string | undefined,
      evidence: nodeData.evidence as CodeEvidence[] | undefined,
      e2eStepData,
    };

    setSelectedNode(nodeWithEvidence);
    setIsEvidencePanelOpen(true);
  }, []);

  // Issue #434: Handle edge click to show HTTP request/response details
  // Issue #438: Enhanced with contextual evidence labels and database info
  const handleEdgeClick = useCallback((edge: Edge, sourceNode: Node | undefined, targetNode: Node | undefined) => {
    if (!sourceNode || !targetNode) return;

    const sourceData = sourceNode.data as Record<string, unknown>;
    const targetData = targetNode.data as Record<string, unknown>;

    // Determine edge type based on node types
    const sourceType = sourceData.type as string;
    const targetType = targetData.type as string;

    let edgeType: 'request' | 'response' | 'callback' | 'database' | 'other' = 'other';
    // Issue #438: Track database direction for query vs result
    let databaseDirection: 'query' | 'result' | undefined = undefined;

    if (sourceType === 'api-call' && (targetType === 'api-route' || targetType === 'backend-logic')) {
      edgeType = 'request';
    } else if ((sourceType === 'api-route' || sourceType === 'backend-logic') && targetType === 'api-response') {
      edgeType = 'response';
    } else if (sourceType === 'api-response' && targetType === 'api-callback') {
      edgeType = 'callback';
    } else if (targetType === 'database') {
      // Query direction: going TO database
      edgeType = 'database';
      databaseDirection = 'query';
    } else if (sourceType === 'database') {
      // Result direction: coming FROM database
      edgeType = 'database';
      databaseDirection = 'result';
    }

    // Issue #438: Determine contextual labels based on edge type
    let sourceLabel = 'Source Code';
    let targetLabel = 'Target Code';

    switch (edgeType) {
      case 'request':
        sourceLabel = 'Calling Code';
        targetLabel = 'Handler Code';
        break;
      case 'response':
        sourceLabel = 'Handler Code';
        targetLabel = 'Callback Code';
        break;
      case 'callback':
        sourceLabel = 'Response Code';
        targetLabel = 'Callback Code';
        break;
      case 'database':
        if (databaseDirection === 'query') {
          sourceLabel = 'Query Code';
          targetLabel = ''; // No target evidence for query
        } else {
          sourceLabel = 'Query Code'; // Show the query that produced the result
          targetLabel = 'Result Handling Code';
        }
        break;
    }

    // Get evidence from both nodes
    const sourceEvidence = sourceData.evidence as CodeEvidence[] | undefined;
    const targetEvidence = targetData.evidence as CodeEvidence[] | undefined;

    // Build edge info
    const edgeInfo: EdgeInfo = {
      id: edge.id,
      type: edgeType,
      sourceNode: {
        id: sourceNode.id,
        type: sourceType || 'unknown',
        label: (sourceData.label as string) || sourceNode.id,
      },
      targetNode: {
        id: targetNode.id,
        type: targetType || 'unknown',
        label: (targetData.label as string) || targetNode.id,
      },
      // Issue #438: Contextual evidence with labels
      sourceEvidence: sourceLabel && sourceEvidence && sourceEvidence.length > 0 ? {
        label: sourceLabel,
        evidence: sourceEvidence,
      } : undefined,
      targetEvidence: targetLabel && targetEvidence && targetEvidence.length > 0 ? {
        label: targetLabel,
        evidence: targetEvidence,
      } : undefined,
    };

    // Combine evidence for backward compatibility
    if (sourceEvidence || targetEvidence) {
      edgeInfo.evidence = [
        ...(sourceEvidence || []),
        ...(targetEvidence || []),
      ];
    }

    // Build request info from api-call node
    // Note: method and endpoint are stored directly on node.data, not in apiCallStepData
    // Handle both: source is api-call OR target is api-call (edge going TO an API call)
    // Issue #435: Include typed fields if available
    if (sourceType === 'api-call') {
      edgeInfo.request = {
        method: (sourceData.method as string) || 'GET',
        endpoint: (sourceData.endpoint as string) || '/unknown',
        pathParams: sourceData.pathParams as string[] | undefined,
        queryParams: sourceData.queryParams as string[] | undefined,
        // Get request schema and fields from api-route target node if available
        bodyFields: (targetData.requestFields as string[]) || (targetData.requestBodyFields as string[]),
        bodySchemaName: (targetData.requestSchemaName as string) || (targetData.requestBodySchemaName as string),
        // Issue #435: Include typed fields
        bodyTypedFields: targetData.requestTypedFields as { name: string; type: string; isOptional?: boolean; isNullable?: boolean }[] | undefined,
      };
    } else if (targetType === 'api-call') {
      // Edge going TO an api-call (e.g., user-action → api-call)
      edgeType = 'request';
      edgeInfo.type = edgeType;
      edgeInfo.request = {
        method: (targetData.method as string) || 'GET',
        endpoint: (targetData.endpoint as string) || '/unknown',
        pathParams: targetData.pathParams as string[] | undefined,
        queryParams: targetData.queryParams as string[] | undefined,
        bodyFields: (targetData.requestFields as string[]) || (targetData.requestBodyFields as string[]),
        bodySchemaName: (targetData.requestSchemaName as string) || (targetData.requestBodySchemaName as string),
        // Issue #435: Include typed fields
        bodyTypedFields: targetData.requestTypedFields as { name: string; type: string; isOptional?: boolean; isNullable?: boolean }[] | undefined,
      };
    }

    // Build response info from api-response node or from api-route source
    // Response data (statusCode, type, fields) is stored directly on node.data or in nested apiRouteStepData
    // Issue #435: Include typed fields if available
    // Issue #438: Also check apiRouteStepData for response fields
    const sourceApiRouteData = sourceData.apiRouteStepData as Record<string, unknown> | undefined;
    const sourceApiResponseInfo = sourceData.apiResponseInfo as Record<string, unknown> | undefined;
    const targetApiResponseInfo = targetData.apiResponseInfo as Record<string, unknown> | undefined;

    if (targetType === 'api-response') {
      // Get response info from api-response node, with fallback to source api-route node
      edgeInfo.response = {
        statusCode: (targetApiResponseInfo?.statusCode as number) || (targetData.statusCode as number) || 200,
        type: (targetApiResponseInfo?.type as string) || (targetData.responseType as string) || (targetData.description as string) || (targetData.label as string) || 'Response',
        bodyFields: (targetData.responseFields as string[]) || (sourceData.responseFields as string[]) || (sourceApiRouteData?.responseFields as string[]),
        contentType: (targetApiResponseInfo?.contentType as string) || (targetData.contentType as string),
        // Issue #435: Include typed fields
        bodyTypedFields: (targetData.responseTypedFields || sourceData.responseTypedFields || sourceApiRouteData?.responseTypedFields) as { name: string; type: string; isOptional?: boolean; isNullable?: boolean }[] | undefined,
      };
    } else if (sourceType === 'api-route') {
      // For edges from api-route, include response fields from the source node
      edgeInfo.response = {
        statusCode: (sourceApiResponseInfo?.statusCode as number) || (sourceData.statusCode as number) || (targetData.statusCode as number) || 200,
        type: (sourceApiResponseInfo?.type as string) || (sourceData.responseType as string) || (targetData.responseType as string) || 'Response',
        bodyFields: (sourceData.responseFields as string[]) || (sourceApiRouteData?.responseFields as string[]) || (targetData.responseFields as string[]),
        contentType: (sourceData.contentType as string) || (targetData.contentType as string),
        // Issue #435: Include typed fields
        bodyTypedFields: (sourceData.responseTypedFields || sourceApiRouteData?.responseTypedFields || targetData.responseTypedFields) as { name: string; type: string; isOptional?: boolean; isNullable?: boolean }[] | undefined,
      };
    }

    // Issue #438: Extract database info when edge involves database node
    if (edgeType === 'database') {
      const databaseNodeData = databaseDirection === 'query' ? targetData : sourceData;
      // Database fields may be at top level or nested in databaseStepData
      const databaseStepData = databaseNodeData.databaseStepData as Record<string, unknown> | undefined;
      edgeInfo.database = {
        operation: databaseNodeData.operation as string | undefined,
        table: databaseNodeData.table as string | undefined,
        model: databaseNodeData.model as string | undefined,
        // Fields and whereFields are in databaseStepData
        fields: (databaseStepData?.fields || databaseNodeData.fields) as string[] | undefined,
        whereFields: (databaseStepData?.whereFields || databaseNodeData.whereFields) as string[] | undefined,
        direction: databaseDirection,
      };
    }

    setSelectedEdge(edgeInfo);
    setIsEdgePanelOpen(true);
  }, []);

  // Issue #404: Handle legend toggle
  const handleLegendToggle = useCallback(() => {
    setShowLegend(prev => !prev);
    rendererRef.current?.toggleLegend();
  }, []);

  // Issue #404: Handle node finder toggle
  const handleNodeFinderToggle = useCallback(() => {
    setShowNodeFinder(prev => !prev);
    rendererRef.current?.toggleNodeFinder();
  }, []);

  // Issue #427: Handle swimlane toggle
  const handleSwimlaneToggle = useCallback(() => {
    const newMode = layoutMode === 'sequential' ? 'swimlane' : 'sequential';
    // Restore the target mode's saved positions (each mode tracks its own)
    const targetPositions = newMode === 'sequential'
      ? sequentialPositionsRef.current
      : swimlanePositionsRef.current;
    setCustomPositions(targetPositions);
    setHasLayoutChanges(false);
    setLayoutMode(newMode);
    // Force top-to-bottom direction for sequential layout
    if (newMode === 'sequential') {
      rendererRef.current?.setDirection('TB');
    }
  }, [layoutMode]);

  // Issue #982: Full-project flow regeneration, triggered from the
  // "stitches changed" banner. Hits the existing generate endpoint
  // (which clears `e2eFlowsStaleSince` on success) and re-fetches.
  const handleRegenerateAll = useCallback(async () => {
    if (!projectId) return;
    setIsRegeneratingAll(true);
    try {
      const r = await apiFetch(`/projects/${projectId}/e2e-flows/generate`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      // Re-fetch to pick up the new diagram + cleared staleness.
      const reread = await apiFetch(`/projects/${projectId}/e2e-flows`, {
        credentials: 'include',
      });
      if (reread.ok) {
        setFlowsData(await reread.json());
      }
    } catch (err) {
      console.error('Full E2E flow regeneration failed', err);
      setError('Failed to regenerate flows');
    } finally {
      setIsRegeneratingAll(false);
    }
  }, [projectId]);

  // Issue #415: Handle flow regeneration
  const handleRegenerate = useCallback(async () => {
    if (!projectId || selectedFlowIndex === null || !flowsWithResolvedNames) return;

    const flowToRegenerate = flowsWithResolvedNames.flows[selectedFlowIndex];
    if (!flowToRegenerate) return;

    setIsRegenerating(true);
    try {
      const result = await api.regenerateE2EFlow(projectId, flowToRegenerate.id);
      if (result.success) {
        // Refresh the flows data to see the changes
        const response = await apiFetch(`/projects/${projectId}/e2e-flows`, {
          credentials: 'include',
        });
        if (response.ok) {
          const data = await response.json();
          setFlowsData({
            diagramId: data.diagramId,
            diagramName: data.diagramName,
            flows: data.flows,
            customNodePositions: data.customNodePositions || null,
            updatedAt: data.updatedAt,
          });
        }
      }
    } catch (err) {
      console.error('Failed to regenerate flow:', err);
      setError('Failed to regenerate flow');
    } finally {
      setIsRegenerating(false);
    }
  }, [projectId, selectedFlowIndex, flowsWithResolvedNames]);

  // Issue #416: Handle viewing a historical version
  const handleViewVersion = useCallback(async (versionId: string) => {
    if (!projectId || selectedFlowIndex === null || !flowsWithResolvedNames) return;

    const currentFlow = flowsWithResolvedNames.flows[selectedFlowIndex];
    if (!currentFlow) return;

    try {
      const versionData = await api.getE2EFlowVersion(projectId, currentFlow.id, versionId);
      setViewingHistoricalVersion({
        versionId: versionData.id,
        version: versionData.version,
        flowData: versionData.flowData as E2EFlowPath,
        createdAt: versionData.createdAt,
      });
      setShowVersionHistory(false); // Close the modal
    } catch (err) {
      console.error('Failed to load version:', err);
      setError('Failed to load historical version');
    }
  }, [projectId, selectedFlowIndex, flowsWithResolvedNames]);

  // Issue #416: Return to viewing the latest/current version
  const handleReturnToLatest = useCallback(() => {
    setViewingHistoricalVersion(null);
  }, []);

  // Issue #416: State for revert in progress
  const [isReverting, setIsReverting] = useState(false);

  // Issue #416: Handle reverting to a historical version
  const handleRevertToVersion = useCallback(async () => {
    if (!projectId || !viewingHistoricalVersion || selectedFlowIndex === null || !flowsWithResolvedNames) return;

    const currentFlow = flowsWithResolvedNames.flows[selectedFlowIndex];
    if (!currentFlow) return;

    setIsReverting(true);
    try {
      const result = await api.revertE2EFlowVersion(
        projectId,
        currentFlow.id,
        viewingHistoricalVersion.versionId
      );

      if (result.success) {
        // Refresh the flows data to see the reverted flow
        const response = await apiFetch(`/projects/${projectId}/e2e-flows`, {
          credentials: 'include',
        });
        if (response.ok) {
          const data = await response.json();
          setFlowsData({
            diagramId: data.diagramId,
            diagramName: data.diagramName,
            flows: data.flows,
            customNodePositions: data.customNodePositions || null,
            updatedAt: data.updatedAt,
          });
        }
        // Exit historical view mode - now viewing the new current version
        setViewingHistoricalVersion(null);
      }
    } catch (err) {
      console.error('Failed to revert version:', err);
      setError('Failed to revert to selected version');
    } finally {
      setIsReverting(false);
    }
  }, [projectId, viewingHistoricalVersion, selectedFlowIndex, flowsWithResolvedNames]);

  // Get selected flow - Issue #377: Use flowsWithResolvedNames to show updated names
  // Issue #416: Use historical version data when viewing an old version
  const selectedFlow = useMemo(() => {
    if (viewingHistoricalVersion) {
      return viewingHistoricalVersion.flowData;
    }
    if (selectedFlowIndex !== null && flowsWithResolvedNames) {
      return flowsWithResolvedNames.flows[selectedFlowIndex];
    }
    return null;
  }, [selectedFlowIndex, flowsWithResolvedNames, viewingHistoricalVersion]);

  // Memoize data prop for E2EFlowRenderer to avoid new object references on every render,
  // which would cause unnecessary useMemo recomputations inside the renderer and
  // can interfere with React Flow's internal drag state tracking.
  const rendererData = useMemo(() => {
    if (!selectedFlow) return { paths: [] as E2EFlowPath[] };
    return { paths: [selectedFlow] };
  }, [selectedFlow]);

  // Issue #404: Calculate E2E flow stats for DiagramInfoPanel
  const e2eFlowStats = useMemo(() => {
    if (!selectedFlow) return null;
    return calculateFlowStats({ paths: [selectedFlow] });
  }, [selectedFlow]);

  // Issue #424: Fetch enhancement history when info panel is opened for a flow without in-memory data
  useEffect(() => {
    if (!isInfoPanelOpen || !selectedFlow?.id || !projectId) return;

    // Skip if we already have in-memory enhancement info for this flow
    if (flowEnhancementInfo[selectedFlow.id]) return;

    const fetchEnhancementHistory = async () => {
      try {
        const history = await api.getE2EFlowEnhancementHistory(projectId, selectedFlow.id, true);
        if (history && history.length > 0) {
          // Get the most recent enhancement
          const latest = history[0];
          if (latest.applied) {
            setFlowEnhancementInfo(prev => ({
              ...prev,
              [selectedFlow.id]: {
                depth: latest.enhancementDepth as 'standard' | 'deep' | 'ultra-deep',
                acceptedCount: latest.enhancements?.length || 0,
                rejectedCount: 0,
                verificationUsed: false,
                questionsAsked: 0,
                verificationDetails: [],
                verificationRevisions: [],
                enhancedAt: latest.createdAt,
                promptSent: latest.promptSent,
                aiResponse: latest.aiResponse,
              }
            }));
          }
        }
      } catch (err) {
        console.error('Failed to fetch enhancement history:', err);
      }
    };

    fetchEnhancementHistory();
  }, [isInfoPanelOpen, selectedFlow?.id, projectId, flowEnhancementInfo]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-4">
        <div className="text-red-500">{error}</div>
        <Link
          to={`/projects/${projectId}`}
          className="text-blue-500 hover:underline"
        >
          Back to project
        </Link>
      </div>
    );
  }

  if (!flowsData || flowsData.flows.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-4">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-slate-800 dark:text-slate-200 mb-2">
            No Interactions Found
          </h2>
          <p className="text-slate-600 dark:text-slate-400 max-w-md">
            This project doesn't have any user interactions detected yet.
            Try generating flows from existing analysis artifacts, or run
            a full analysis to discover interactions.
          </p>
        </div>
        {/* #1036: explicit Generate button so projects that finished
            analysis with an empty e2eFlow row aren't stuck — the
            "Regenerate now" CTA at the top doesn't render in this empty
            state. */}
        <button
          onClick={handleRegenerateAll}
          disabled={isRegeneratingAll}
          data-testid="e2e-flows-generate-empty"
          className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 transition-colors"
        >
          {isRegeneratingAll ? 'Generating…' : 'Generate flows'}
        </button>
        <Link
          to={`/projects/${projectId}`}
          className="text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 underline"
        >
          Go to project
        </Link>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-slate-50 dark:bg-slate-900">
      {/* Header - Different for catalog vs diagram view */}
      <div className="flex items-center justify-between px-4 py-3 bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">
        <div className="flex items-center gap-3">
          {viewMode === 'catalog' ? (
            // Catalog view: back to project
            <Link
              to={`/projects/${projectId}`}
              className="text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
              title="Back to Project"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </Link>
          ) : (
            // Issue #356: Diagram view: back to catalog
            <button
              onClick={handleBackToCatalog}
              className="flex items-center gap-2 text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 transition-colors"
              title="Back to Interactions"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              <span className="text-sm font-medium">Back to Interactions</span>
            </button>
          )}
          <div className="border-l border-slate-300 dark:border-slate-600 pl-3">
            {viewMode === 'catalog' ? (
              <>
                <h1 className="text-lg font-semibold text-slate-800 dark:text-slate-200 flex items-center gap-2">
                  E2E User Interactions
                  {/* #1074 F1: signal that interaction-detection recall is
                      sub-30% on typical React/Next code so users don't
                      mistake a missing flow for a bug they should report. */}
                  <span className="text-[10px] px-1.5 py-0.5 rounded uppercase tracking-wide bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300">
                    Preview
                  </span>
                </h1>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  {project?.name} - {filteredCatalogFlows.length}{flowFilter !== 'all' ? ` ${flowFilter}` : ''} of {visibleFlowsData?.flows.length ?? 0} interactions
                  {internalInteractionCount > 0 && !showInternalInteractions && (
                    <span className="text-slate-400"> ({internalInteractionCount} internal hidden)</span>
                  )}
                </p>
              </>
            ) : (
              // Issue #356: Show selected flow name in diagram view
              <>
                {/* Issue #647: Breadcrumb trail when navigated from artifacts pages */}
                {breadcrumb && breadcrumb.length > 0 && (
                  <div className="flex items-center gap-1 text-xs text-slate-400 dark:text-slate-500 mb-0.5 flex-wrap">
                    {breadcrumb.map((crumb, i) => (
                      <span key={i} className="flex items-center gap-1">
                        {i > 0 && <span>/</span>}
                        <Link to={crumb.url} className="hover:text-blue-500 dark:hover:text-blue-400 transition-colors">{crumb.label}</Link>
                      </span>
                    ))}
                    <span>/</span>
                    <span className="text-slate-500 dark:text-slate-400">E2E Flows</span>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <span className={`px-2 py-0.5 text-xs font-medium rounded ${
                    selectedFlow?.complete
                      ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                      : 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300'
                  }`}>
                    {selectedFlow?.complete ? 'Complete E2E Flow' : 'UI-Only'}
                  </span>
                  <h1 className="text-lg font-semibold text-slate-800 dark:text-slate-200">
                    {selectedFlow?.name || 'No Flow Selected'}
                  </h1>
                </div>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  {selectedFlow?.steps.length || 0} steps
                  {selectedFlow?.description && ` - ${selectedFlow.description}`}
                </p>
              </>
            )}
          </div>
        </div>

        {/* Right side actions */}
        <div className="flex items-center gap-2">
          {viewMode === 'catalog' && (
            // Issue #363 & #364 & #556: Catalog action buttons
            <>
              {/* Issue #542: Internal interactions toggle */}
              {internalInteractionCount > 0 && (
                <button
                  onClick={() => setShowInternalInteractions(v => !v)}
                  className={`px-2 py-1 text-xs rounded-lg transition-colors ${
                    showInternalInteractions
                      ? 'text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/30'
                      : 'text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700'
                  }`}
                  title={showInternalInteractions
                    ? 'Hide internal interactions (state setters, hover handlers, sub-component behaviors)'
                    : 'Show internal interactions (state setters, hover handlers, sub-component behaviors)'}
                >
                  {showInternalInteractions ? 'Hide' : 'Show'} internal ({internalInteractionCount})
                </button>
              )}
              {/* Issue #859 / #878: three-way flow filter toggle */}
              {visibleFlowsData?.flows.some((f: any) => getClassification(f) !== undefined) && (() => {
                // #1310 PR-J — when ?catalog=1, derive counts from the
                // graph-backed catalog endpoint instead of scanning
                // `visibleFlowsData.flows[].steps`. Falls back to the
                // legacy counting path when the endpoint hasn't loaded
                // (or the flag is off).
                const cleanCount = catalogData
                  ? catalogData.filter(c => c.classification === 'clean').length
                  : visibleFlowsData.flows.filter((fl: any) => getClassification(fl) === 'clean').length;
                const reviewCount = catalogData
                  ? catalogData.filter(c => c.classification === 'needs-review').length
                  : visibleFlowsData.flows.filter((fl: any) => getClassification(fl) === 'needs-review').length;
                const noApiCount = catalogData
                  ? catalogData.filter(c => c.classification === 'no-api').length
                  : visibleFlowsData.flows.filter((fl: any) => getClassification(fl) === 'no-api').length;
                // #1339 item 5 — dangling category.
                const danglingCount = catalogData
                  ? catalogData.filter((c: any) => c.classification === 'dangling').length
                  : visibleFlowsData.flows.filter((fl: any) => getClassification(fl) === 'dangling').length;
                const label = (f: FlowFilter) => {
                  if (f === 'all') return 'All';
                  if (f === 'clean') return `Clean (${cleanCount})`;
                  if (f === 'needs-review') return `Needs review (${reviewCount})`;
                  if (f === 'dangling') return `Dangling (${danglingCount})`;
                  return `No API calls (${noApiCount})`;
                };
                const activeStyle = (f: FlowFilter) => {
                  if (f === 'clean') return 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300 font-medium';
                  if (f === 'needs-review') return 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 font-medium';
                  if (f === 'no-api') return 'bg-slate-200 dark:bg-slate-600 text-slate-600 dark:text-slate-300 font-medium';
                  // #1339 item 5 — dangling uses rose to signal "actionable":
                  // unlike needs-review (heuristic), dangling means the stitch
                  // never resolved and the user can intervene (add a source,
                  // accept an AI proposal, mark as external).
                  if (f === 'dangling') return 'bg-rose-100 dark:bg-rose-900/40 text-rose-700 dark:text-rose-300 font-medium';
                  return 'bg-slate-200 dark:bg-slate-600 text-slate-700 dark:text-slate-200 font-medium';
                };
                // #1339 item 5 — hide the Dangling chip when zero (most
                // single-source / fully-stitched projects). Keeps the
                // chip strip clean for the common case.
                const buckets = (['all', 'clean', 'needs-review', 'no-api'] as const).slice() as FlowFilter[];
                if (danglingCount > 0) buckets.push('dangling');
                return (
                  <div className="flex items-center gap-0.5 rounded-lg border border-slate-200 dark:border-slate-600 overflow-hidden text-xs">
                    {buckets.map(f => (
                      <button
                        key={f}
                        onClick={() => setFlowFilter(f)}
                        className={`px-2 py-1 transition-colors ${
                          flowFilter === f
                            ? activeStyle(f)
                            : 'text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700'
                        }`}
                      >
                        {label(f)}
                      </button>
                    ))}
                  </div>
                );
              })()}
              {/* Issue #818: FCG/BCG (api-call → route) stitch review entry point */}
              <StitchReviewBadge projectId={projectId} />
              {/* Issue #963: UI handler → api-call stitch review entry point — peer-level */}
              <ClickStitchReviewBadge projectId={projectId} />
              {/* Issue #556 / #995 / #996: Callers & Endpoints panel toggle. */}
              <button
                onClick={() => setShowCallersEndpoints(v => !v)}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                  showCallersEndpoints
                    ? 'bg-slate-200 dark:bg-slate-600 text-slate-700 dark:text-slate-200'
                    : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700'
                }`}
                title="Toggle the Callers & Endpoints panel"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                Callers &amp; Endpoints
                {(activeCaller || activeEndpoint) && (
                  <span className="ml-1 px-1.5 py-0.5 text-xs bg-blue-500 text-white rounded-full">1</span>
                )}
              </button>
              <button
                onClick={() => setShowBulkNaming(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 hover:bg-indigo-100 dark:hover:bg-indigo-900/50 rounded-lg transition-colors"
                title="Generate AI names for all interactions"
              >
                <span>🤖</span>
                AI Naming
              </button>
              <button
                onClick={() => setShowFeedbackHistory(true)}
                className="p-1.5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                title="Feedback History"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </button>
              <button
                onClick={() => setShowAISettings(true)}
                className="p-1.5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                title="AI Naming Settings"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </button>
            </>
          )}
          {viewMode === 'diagram' && (
            // Issue #354: Fit All button in diagram view
            // Issue #392: Enhance with AI button
            // Issue #404: Info button and direction controls
            <>
              {/* Info Button */}
              <button
                onClick={() => setIsInfoPanelOpen(true)}
                className="p-1.5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                title="Diagram Info"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </button>


              {/* Legend Toggle */}
              <button
                onClick={handleLegendToggle}
                className={`p-1.5 rounded-lg transition-colors ${
                  showLegend
                    ? 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30'
                    : 'text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700'
                }`}
                title={showLegend ? 'Hide Legend' : 'Show Legend'}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                </svg>
              </button>

              {/* Find Node Toggle */}
              <button
                onClick={handleNodeFinderToggle}
                className={`p-1.5 rounded-lg transition-colors ${
                  showNodeFinder
                    ? 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30'
                    : 'text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700'
                }`}
                title={showNodeFinder ? 'Hide Node Finder' : 'Find Node'}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </button>

              {/* Issue #427: Swimlane Layout Toggle */}
              <button
                onClick={handleSwimlaneToggle}
                className={`p-1.5 rounded-lg transition-colors ${
                  layoutMode === 'swimlane'
                    ? 'bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-400'
                    : 'text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700'
                }`}
                title={layoutMode === 'swimlane' ? 'Switch to Sequential Layout' : 'Switch to Swimlane Layout (Client | Server | Database)'}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" />
                </svg>
              </button>

              {/* Zoom Controls */}
              <div className="flex items-center bg-slate-100 dark:bg-slate-700 rounded-lg overflow-hidden">
                <button
                  onClick={() => rendererRef.current?.zoomIn()}
                  className="p-1.5 text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors"
                  title="Zoom In"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                  </svg>
                </button>
                <button
                  onClick={() => rendererRef.current?.zoomOut()}
                  className="p-1.5 text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors"
                  title="Zoom Out"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM13 10H7" />
                  </svg>
                </button>
              </div>

              {/* Save/Reset Layout - only show when there are changes */}
              {hasLayoutChanges && (
                <div className="flex items-center gap-1 bg-amber-50 dark:bg-amber-900/20 px-2 py-1 rounded-lg border border-amber-200 dark:border-amber-800">
                  <span className="text-xs text-amber-600 dark:text-amber-400">Layout modified</span>
                  <button
                    onClick={handleSaveLayout}
                    className="px-2 py-0.5 text-xs font-medium bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
                    title="Save layout changes"
                  >
                    Save
                  </button>
                  <button
                    onClick={handleResetLayout}
                    className="px-2 py-0.5 text-xs font-medium bg-slate-200 dark:bg-slate-600 text-slate-600 dark:text-slate-200 rounded hover:bg-slate-300 dark:hover:bg-slate-500 transition-colors"
                    title="Reset to auto layout"
                  >
                    Reset
                  </button>
                </div>
              )}

              {/* Issue #415: Version History Button */}
              <button
                onClick={() => setShowVersionHistory(true)}
                className="p-1.5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                title="Version History"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </button>

              {/* Issue #415: Regenerate Button */}
              <button
                onClick={handleRegenerate}
                disabled={isRegenerating || !!viewingHistoricalVersion}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400 hover:bg-green-100 dark:hover:bg-green-900/50 rounded-lg transition-colors disabled:opacity-50"
                title={viewingHistoricalVersion ? "Return to current version to regenerate" : "Regenerate this flow"}
              >
                {isRegenerating ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-600" />
                    <span>Regenerating...</span>
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    <span>Regenerate</span>
                  </>
                )}
              </button>

              {/* Issue #415: Export Menu */}
              {projectId && selectedFlow && (
                <E2EFlowExportMenu
                  projectId={projectId}
                  flowId={selectedFlow.id}
                  flowName={selectedFlow.name || 'flow'}
                />
              )}

              <button
                onClick={() => setShowEnhancement(true)}
                disabled={!!viewingHistoricalVersion}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium bg-purple-50 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 hover:bg-purple-100 dark:hover:bg-purple-900/50 rounded-lg transition-colors disabled:opacity-50"
                title={viewingHistoricalVersion ? "Return to current version to enhance" : "Enhance this flow with AI"}
              >
                <span>✨</span>
                Enhance with AI
              </button>
              {/* #1074 G1 (#648): one-click copy of the flow as Markdown so
                  users can paste it into any LLM workflow. */}
              <button
                onClick={async () => {
                  if (!selectedFlow) return;
                  const md = formatFlowAsMarkdown(selectedFlow);
                  try {
                    await navigator.clipboard.writeText(md);
                    setCopyFeedback('Copied!');
                    setTimeout(() => setCopyFeedback(null), 1500);
                  } catch {
                    setCopyFeedback('Copy failed');
                    setTimeout(() => setCopyFeedback(null), 1500);
                  }
                }}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/50 rounded-lg transition-colors"
                title="Copy this flow as Markdown for LLM context"
              >
                <span>📋</span>
                {copyFeedback ?? 'Copy as LLM Context'}
              </button>
              <button
                onClick={() => rendererRef.current?.fitView()}
                className="px-3 py-1.5 text-sm font-medium bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600 rounded-lg transition-colors"
                title="Fit all nodes in view"
              >
                Fit All
              </button>
            </>
          )}
        </div>
      </div>

      {/* Issue #982: stale-flows banner. Click stitch mutations
          mark flows stale; one-click regen from here. */}
      {flowsData?.e2eFlowsStaleSince && (
        <div
          data-testid="e2e-flows-stale-banner"
          className="flex items-center justify-between gap-3 px-4 py-2 bg-amber-50 dark:bg-amber-900/20 border-b border-amber-200 dark:border-amber-800 text-sm"
        >
          <div className="text-amber-800 dark:text-amber-300">
            <span className="font-medium">Stitches changed</span> since these flows were generated
            {' · '}
            <Link
              to={`/projects/${projectId}/click-stitch-review`}
              className="underline hover:text-amber-900 dark:hover:text-amber-200"
            >
              Review changes
            </Link>
          </div>
          <button
            onClick={handleRegenerateAll}
            disabled={isRegeneratingAll}
            data-testid="e2e-flows-regenerate-all"
            className="px-3 py-1 text-xs font-medium rounded bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50 whitespace-nowrap"
          >
            {isRegeneratingAll ? 'Regenerating…' : 'Regenerate now'}
          </button>
        </div>
      )}

      {/* Pipeline stats line — kept as quiet ops telemetry now that the
          legacy generator was deleted in #1060-9B and the toggle is gone
          (vestigial after the migration completed). The 30%-recall
          honesty banner was also removed: post-#1060 + cross-repo
          rule-port work (#1311-#1335) the recall claim no longer holds. */}
      {flowsData.newPipelineStats && (
        <div className="px-6 py-1 text-[11px] border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/30 text-slate-500 dark:text-slate-400">
          <span>{flowsData.newPipelineStats.triggersDiscovered} triggers / {flowsData.newPipelineStats.handlersResolved} resolved / {flowsData.newPipelineStats.stepsEmitted} steps</span>
        </div>
      )}

      {/* Main Content - Issue #355: Full width for both views */}
      <div className="flex-1 flex overflow-hidden">
        {viewMode === 'catalog' ? (
          // Catalog view: Callers & Endpoints panel (or collapsed strip) + catalog.
          <div className="flex flex-1 overflow-hidden">
            {showCallersEndpoints ? (
              <CallersEndpointsPanel
                flows={flowsData.flows}
                activeCaller={activeCaller}
                activeEndpoint={activeEndpoint}
                onSelectCaller={setActiveCaller}
                onSelectEndpoint={setActiveEndpoint}
                onCollapse={() => setShowCallersEndpoints(false)}
              />
            ) : (
              <CallersEndpointsCollapsedStrip
                onExpand={() => setShowCallersEndpoints(true)}
              />
            )}
            <div className="flex-1 overflow-auto">
              {/* veodiagram#1060 iteration 3A — graceful fallback. The new
                  pipeline is now the default, but a project regenerated
                  before this PR landed has `newPipelineFlows === null`.
                  Rather than show the "dual-emit not enabled" empty state
                  silently, fall back to legacy and let the banner above
                  hint at the activation step.

                  Playwright-loop iter-4 extension: also fall back when
                  newPipelineFlows is `[]` (dual-emit ran, found zero
                  triggers) AND legacy `flows` has entries. This is the
                  TS-only-backend case (e.g. TCC): the new pipeline only
                  knows about JSX/Vue/Angular triggers, so it correctly
                  emits 0, but the legacy heuristic catches custom
                  event-binder patterns the new pipeline does not yet
                  cover. Showing the legacy catalog instead of "No
                  triggers discovered" preserves visibility of the 23
                  real flows that exist. The user can still opt in to
                  the empty new-pipeline view via the existing
                  `switch to legacy` toggle's inverse — clicking through
                  to a project URL without `?flowSubsystem=legacy`. */}
              {/*
                #1060 six3 follow-on — the UI is the same regardless of
                pipeline. The new pipeline's output is already projected
                through `flowRenderAdapter` into `flowsData.flows`
                (the legacy E2EFlowPath shape) before this point, so
                `InteractionCatalog` reads identical data whether the user
                toggled `?flowSubsystem=legacy` or stayed on default. The
                green banner above still shows the new-pipeline stats.
                Diagnostic-only `NewPipelineCatalog` is retired.
              */}
              <InteractionCatalog
                flows={filteredCatalogFlows}
                onSelectFlow={handleFlowSelect}
                className=""
                projectId={projectId}
                onNameUpdated={handleNameUpdated}
                refreshTrigger={namesRefreshTrigger}
              />
              {/* veodiagram-issues#63 — server-rendered screens
                  section. Renders ABOVE SystemRoutes so user-facing
                  pages appear before system-only routes. Hides when
                  the array is absent OR empty (SPA projects). */}
              {flowsData.screensDetected && flowsData.screensDetected.length > 0 && (
                <ScreensSection screens={flowsData.screensDetected} />
              )}
              {/* Issue #784: System-triggered routes section.
                  veodiagram-issues#64 R1#3 — pass screensDetected so
                  the inline badge can show "+N more" on multiplexed
                  routes. */}
              {flowsData.systemRoutes && flowsData.systemRoutes.length > 0 && (
                <SystemRoutesSection
                  routes={flowsData.systemRoutes}
                  screens={flowsData.screensDetected ?? []}
                />
              )}
            </div>
          </div>
        ) : (
          // Diagram view: Full width canvas (no sidebar)
          // Using explicit height calculation to ensure React Flow has dimensions
          <div
            className="flex-1 bg-slate-100 dark:bg-slate-900 flex flex-col"
            style={{ height: 'calc(100vh - 120px)', width: '100%' }}
          >
            {/* Issue #416: Historical version banner */}
            {viewingHistoricalVersion && (
              <div className="flex items-center justify-between px-4 py-2 bg-amber-50 dark:bg-amber-900/30 border-b border-amber-200 dark:border-amber-700">
                <div className="flex items-center gap-2 text-amber-800 dark:text-amber-200">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className="text-sm font-medium">
                    Viewing Version {viewingHistoricalVersion.version}
                  </span>
                  <span className="text-sm text-amber-600 dark:text-amber-400">
                    ({new Date(viewingHistoricalVersion.createdAt).toLocaleDateString()})
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={handleRevertToVersion}
                    disabled={isReverting}
                    className="px-3 py-1 text-sm font-medium text-white bg-blue-500 hover:bg-blue-600 disabled:opacity-50 rounded-lg transition-colors flex items-center gap-1.5"
                    title="Make this version the current version"
                  >
                    {isReverting ? (
                      <>
                        <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white" />
                        <span>Reverting...</span>
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                        </svg>
                        <span>Revert to This</span>
                      </>
                    )}
                  </button>
                  <button
                    onClick={handleReturnToLatest}
                    disabled={isReverting}
                    className="px-3 py-1 text-sm font-medium text-amber-700 dark:text-amber-300 bg-amber-100 dark:bg-amber-800/50 hover:bg-amber-200 dark:hover:bg-amber-800 disabled:opacity-50 rounded-lg transition-colors"
                  >
                    Return to Current
                  </button>
                </div>
              </div>
            )}
            {selectedFlow ? (
              <div className="flex-1 min-h-0">
                <E2EFlowRenderer
                  ref={rendererRef}
                  data={rendererData}
                  readOnly={true}
                  editable={!viewingHistoricalVersion}
                  customPositions={customPositions}
                  layoutMode={layoutMode}
                  onLayoutChange={handleLayoutChange}
                  onSaveLayout={handleSaveLayout}
                  onLayoutModeChange={setLayoutMode}
                  onNodeClick={handleNodeClick}
                  onEdgeClick={handleEdgeClick}
                  className="h-full w-full"
                />
              </div>
            ) : (
              <div className="flex-1 flex flex-col items-center justify-center text-slate-500 dark:text-slate-400 gap-4">
                <p>No interaction selected</p>
                <button
                  onClick={handleBackToCatalog}
                  className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                >
                  Go to Interactions Catalog
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Stats Footer - Only show in catalog view */}
      {viewMode === 'catalog' && (
        <div className="px-4 py-2 bg-white dark:bg-slate-800 border-t border-slate-200 dark:border-slate-700">
          <div className="flex items-center gap-6 text-sm text-slate-600 dark:text-slate-400">
            <span>
              <strong className="text-slate-800 dark:text-slate-200">{visibleFlowsData?.flows.length ?? 0}</strong> interactions
              {internalInteractionCount > 0 && (
                <span className="text-slate-400 ml-1 text-xs">
                  ({internalInteractionCount} internal {showInternalInteractions ? 'shown' : 'hidden'})
                </span>
              )}
            </span>
            <span>
              <strong className="text-slate-800 dark:text-slate-200">
                {visibleFlowsData?.flows.filter(f => f.complete).length ?? 0}
              </strong> complete E2E flows
            </span>
            <span>
              <strong className="text-slate-800 dark:text-slate-200">
                {visibleFlowsData?.flows.reduce((sum, f) => sum + f.steps.length, 0) ?? 0}
              </strong> total steps
            </span>
            <span className="ml-auto text-xs">
              Last updated: {new Date(flowsData.updatedAt).toLocaleDateString()}
            </span>
          </div>
        </div>
      )}

      {/* Issue #363: Bulk AI Naming Modal */}
      {showBulkNaming && projectId && (
        <BulkAINamingModal
          projectId={projectId}
          interactions={flowsData.flows}
          onComplete={handleBulkNamingComplete}
          onSkip={() => setShowBulkNaming(false)}
          onClose={() => setShowBulkNaming(false)}
        />
      )}

      {/* Issue #364: AI Naming Settings Modal */}
      {showAISettings && projectId && (
        <AINamingSettings
          projectId={projectId}
          onClose={() => setShowAISettings(false)}
        />
      )}

      {/* Issue #365: Feedback History Panel */}
      {showFeedbackHistory && projectId && (
        <FeedbackHistoryPanel
          projectId={projectId}
          onClose={() => setShowFeedbackHistory(false)}
        />
      )}

      {/* Issue #392: AI Flow Enhancement Modal */}
      {showEnhancement && projectId && selectedFlowIndex !== null && flowsData.flows[selectedFlowIndex] && (
        <AIFlowEnhancementModal
          projectId={projectId}
          flow={flowsData.flows[selectedFlowIndex]}
          onComplete={async (info) => {
            // Issue #411: Store enhancement info for display in info panel
            if (info) {
              const flowId = flowsData.flows[selectedFlowIndex].id;
              setFlowEnhancementInfo(prev => ({
                ...prev,
                [flowId]: info,
              }));
            }
            setShowEnhancement(false);
            // Refresh the flows data to see the changes
            if (projectId) {
              try {
                const response = await apiFetch(`/projects/${projectId}/e2e-flows`, {
                  credentials: 'include',
                });
                if (response.ok) {
                  const data = await response.json();
                  setFlowsData({
                    diagramId: data.diagramId,
                    diagramName: data.diagramName,
                    flows: data.flows,
                    customNodePositions: data.customNodePositions || null,
                    updatedAt: data.updatedAt,
                  });
                }
              } catch (err) {
                console.error('Failed to refresh flows:', err);
              }
            }
          }}
          onClose={() => setShowEnhancement(false)}
        />
      )}

      {/* Issue #415: Version History Modal */}
      {showVersionHistory && projectId && selectedFlowIndex !== null && flowsWithResolvedNames && (
        <E2EFlowVersionHistory
          projectId={projectId}
          flowId={flowsWithResolvedNames.flows[selectedFlowIndex]?.id || ''}
          flowName={flowsWithResolvedNames.flows[selectedFlowIndex]?.name || 'Flow'}
          onClose={() => setShowVersionHistory(false)}
          onViewVersion={handleViewVersion}
        />
      )}

      {/* Issue #404: Evidence Panel for showing full node details */}
      <EvidencePanel
        node={selectedNode}
        isOpen={isEvidencePanelOpen}
        onClose={() => {
          setIsEvidencePanelOpen(false);
          setSelectedNode(null);
        }}
      />

      {/* Issue #434: Edge Detail Panel for showing HTTP request/response details */}
      <EdgeDetailPanel
        edge={selectedEdge}
        isOpen={isEdgePanelOpen}
        onClose={() => {
          setIsEdgePanelOpen(false);
          setSelectedEdge(null);
        }}
      />

      {/* Issue #404: Diagram Info Panel with E2E flow stats, logic, and evidence */}
      <DiagramInfoPanel
        diagramType="e2eFlow"
        isOpen={isInfoPanelOpen}
        onClose={() => setIsInfoPanelOpen(false)}
        nodeCount={selectedFlow?.steps.length || 0}
        edgeCount={(selectedFlow?.steps.length || 1) - 1}
        e2eFlowInfo={{
          flow: selectedFlow,
          stats: e2eFlowStats,
        }}
        // Issue #411: Pass AI enhancement info for transparency
        aiEnhancementInfo={selectedFlow?.id && flowEnhancementInfo[selectedFlow.id] ? {
          enhanced: true,
          depth: flowEnhancementInfo[selectedFlow.id].depth,
          acceptedCount: flowEnhancementInfo[selectedFlow.id].acceptedCount,
          rejectedCount: flowEnhancementInfo[selectedFlow.id].rejectedCount,
          verificationUsed: flowEnhancementInfo[selectedFlow.id].verificationUsed,
          questionsAsked: flowEnhancementInfo[selectedFlow.id].questionsAsked,
          verificationDetails: flowEnhancementInfo[selectedFlow.id].verificationDetails,
          verificationRevisions: flowEnhancementInfo[selectedFlow.id].verificationRevisions,
          enhancedAt: flowEnhancementInfo[selectedFlow.id].enhancedAt,
          // Issue #424: Include prompt/response for history viewing
          promptSent: flowEnhancementInfo[selectedFlow.id].promptSent,
          aiResponse: flowEnhancementInfo[selectedFlow.id].aiResponse,
        } : undefined}
      />
    </div>
  );
}

export default E2EFlowsPage;
